import { IntegrationsResponse } from "features/integrations/integrations";
import { SPECIAL_TRIGGERS } from "./constants";

export function formatFreqAndTime(row: IntegrationsResponse): string[] {
  const { frequency, timezone } = row;

  const now = new Date();
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "short",
  });
  const tzName =
    dtf.formatToParts(now).find((p) => p.type === "timeZoneName")?.value ?? "";

  const frequencies = Array.isArray(frequency)
    ? frequency
    : String(frequency).split(",");

  return frequencies.map((f) => {
    const trimmed = f.trim();

    if (SPECIAL_TRIGGERS.includes(trimmed)) {
      return trimmed;
    }

    return `${trimmed} ${tzName}`;
  });
}
export function getTimezoneName(timezone: string, locale = "en-US"): string {
  try {
    const now = new Date();
    const dtf = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: "short",
    });

    return (
      dtf.formatToParts(now).find((p) => p.type === "timeZoneName")?.value ?? ""
    );
  } catch (e) {
    console.error("Invalid timezone:", timezone, e);
    return "";
  }
}

export function formatWeekday(timestampSec: number, locale = "en-US"): string {
  const date = new Date(timestampSec * 1000);
  return date.toLocaleDateString(locale, {
    weekday: "long",
  });
}

export function formatDateTime(timestampSec: number, locale = "en-US"): string {
  const date = new Date(timestampSec * 1000);
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatWeekdayInTimezone(
  timestampSec: number,
  timezone: string,
  locale = "en-US",
): string {
  const date = new Date(timestampSec * 1000);
  return date.toLocaleDateString(locale, {
    weekday: "long",
    timeZone: timezone,
  });
}

export function formatDateTimeInTimezone(
  timestampSec: number,
  timezone: string,
  locale = "en-US",
): string {
  const date = new Date(timestampSec * 1000);
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  });
}

/**
 * Format integration date/time: weekday, American date (MM/DD/YYYY), then time.
 * e.g. "Tuesday, 02/03/2026 at 5:14 PM"
 */
export function formatIntegrationDateTime(
  timestampSec: number,
  timezone?: string,
  locale = "en-US",
): string {
  const date = new Date(timestampSec * 1000);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timezone && { timeZone: timezone }),
  };
  const parts = new Intl.DateTimeFormat(locale, opts).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const weekday = get("weekday");
  const month = get("month");
  const day = get("day");
  const year = get("year");
  const hour = get("hour");
  const minute = get("minute");
  const dayPeriod = get("dayPeriod");
  const timeStr = `${hour}:${minute} ${dayPeriod.toUpperCase()}`;
  return `${weekday}, ${month}/${day}/${year} at ${timeStr}`;
}

export interface Range {
  from: string;
  to?: string;
}

export function getTodayRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
    0,
    0,
    0,
    0,
  );
  return {
    from: Math.floor(start.getTime() / 1000).toString(),
    to: Math.floor(end.getTime() / 1000).toString(),
  };
}

export function getYesterdayRange(): { from: string; to: string } {
  const today = new Date();
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 1,
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
    0,
    0,
  );

  return {
    from: Math.floor(start.getTime() / 1000).toString(),
    to: Math.floor(end.getTime() / 1000).toString(),
  };
}

export function getYesterdayTimestamp(): number {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return Math.floor(d.getTime() / 1000);
}

export function getWeekRange(offsetWeeks: number = 0): {
  from: string;
  to: string;
} {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - day + 1 + offsetWeeks * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return {
    from: Math.floor(monday.getTime() / 1000).toString(),
    to: Math.floor(sunday.getTime() / 1000).toString(),
  };
}

export function getThisWeekRange() {
  return getWeekRange(0);
}

export function getNextWeekRange() {
  return getWeekRange(1);
}
